import React, { useEffect, useMemo, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Table,
  Badge,
  Spinner,
  Tabs,
  Tab,
} from "react-bootstrap";
import { ToastContainer, toast } from "react-toastify";
import { FaBed, FaFileExcel, FaSync, FaCheckCircle } from "react-icons/fa";
import inventarioAlojamientoLogiflexApi from "../../../services/api/inventarioAlojamientoLogiflexApi";
import * as XLSX from "xlsx";
import "react-toastify/dist/ReactToastify.css";

const badgeVariant = (accion) => {
  switch (accion) {
    case "OK":
      return "success";
    case "CREAR":
      return "primary";
    case "REACTIVAR":
      return "info";
    case "INACTIVAR":
      return "danger";
    case "IGNORAR_EXCEL_INACTIVO":
      return "secondary";
    default:
      return "secondary";
  }
};

const InventarioAlojamientoLogiflex = () => {
  const [selectedProject, setSelectedProject] = useState(null);
  const [file, setFile] = useState(null);
  const [hotel, setHotel] = useState("CAMPAMENTO");
  const [loading, setLoading] = useState(false);
  const [aplicando, setAplicando] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [busqueda, setBusqueda] = useState("");
  const [tab, setTab] = useState("crear");
  const [opciones, setOpciones] = useState({
    crear: true,
    reactivar: true,
    inactivar: true,
  });

  useEffect(() => {
    try {
      const stored = localStorage.getItem("selectedProject");
      if (stored && stored !== "undefined") {
        setSelectedProject(JSON.parse(stored));
      }
    } catch (e) {
      toast.error("Error al cargar el proyecto");
    }
  }, []);

  const resumen = resultado?.resumen;

  const filtrar = (filas, accion) => {
    const list = (filas || []).filter((f) => {
      if (accion && f.accion !== accion) return false;
      if (!busqueda.trim()) return true;
      const q = busqueda.trim().toLowerCase();
      return [f.zona, f.modulo, f.habitacion, f.habitacion_nombre, f.cama, f.codigo, f.motivo]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(q);
    });
    return list;
  };

  const camasCrear = useMemo(
    () => filtrar(resultado?.camas, "CREAR"),
    [resultado, busqueda]
  );
  const camasInactivar = useMemo(
    () => filtrar(resultado?.camas, "INACTIVAR"),
    [resultado, busqueda]
  );
  const camasReactivar = useMemo(
    () => filtrar(resultado?.camas, "REACTIVAR"),
    [resultado, busqueda]
  );
  const camasOk = useMemo(
    () => filtrar(resultado?.camas, "OK"),
    [resultado, busqueda]
  );
  const habsCrear = useMemo(
    () => filtrar(resultado?.habitaciones, "CREAR"),
    [resultado, busqueda]
  );
  const habsInactivar = useMemo(
    () => filtrar(resultado?.habitaciones, "INACTIVAR"),
    [resultado, busqueda]
  );

  const handleFileChange = (e) => {
    const selected = e.target.files[0];
    if (!selected) return;
    const ext = selected.name.split(".").pop().toLowerCase();
    if (!["xlsx", "xls"].includes(ext)) {
      toast.error("Seleccione un Excel (.xlsx / .xls)");
      e.target.value = "";
      return;
    }
    setFile(selected);
    setResultado(null);
  };

  const validar = async () => {
    if (!selectedProject?.id) {
      toast.error("Seleccione un proyecto");
      return;
    }
    if (!file) {
      toast.warning("Seleccione el Excel de inventario Logiflex");
      return;
    }
    setLoading(true);
    try {
      const response = await inventarioAlojamientoLogiflexApi.validar(
        file,
        selectedProject.id,
        hotel
      );
      setResultado(response.data);
      if (response.data?.success === false) {
        toast.warning(response.data.message || "Validación con observaciones");
      } else {
        toast.success(response.data?.message || "Validación completada");
        setTab("crear");
      }
    } catch (error) {
      console.error(error);
      toast.error(
        error.response?.data?.detail ||
          error.response?.data?.message ||
          error.message ||
          "Error al validar"
      );
      setResultado(null);
    } finally {
      setLoading(false);
    }
  };

  const aplicar = async () => {
    if (!file || !selectedProject?.id || !resultado?.success) {
      toast.warning("Primero valide el Excel");
      return;
    }
    const ok = window.confirm(
      "Se crearán / reactivarán / inactivarán camas según el diff. ¿Continuar?"
    );
    if (!ok) return;

    setAplicando(true);
    try {
      const response = await inventarioAlojamientoLogiflexApi.aplicar(
        file,
        selectedProject.id,
        { hotel, ...opciones }
      );
      const data = response.data;
      if (data?.validacion) {
        setResultado(data.validacion);
      }
      toast.success(data?.message || "Cambios aplicados");
    } catch (error) {
      console.error(error);
      toast.error(
        error.response?.data?.detail ||
          error.response?.data?.message ||
          error.message ||
          "Error al aplicar"
      );
    } finally {
      setAplicando(false);
    }
  };

  const exportar = () => {
    if (!resultado?.camas?.length) {
      toast.warning("No hay resultados para exportar");
      return;
    }
    const rows = resultado.camas.map((f) => ({
      Accion: f.accion,
      Zona: f.zona || "",
      Modulo: f.modulo || "",
      Habitacion: f.habitacion || "",
      "Habitacion Logiflex": f.habitacion_nombre || "",
      Cama: f.cama || "",
      Codigo: f.codigo || "",
      "ID Proveedor": f.id_proveedor || "",
      "Cama ID SGC": f.cama_id ?? "",
      Motivo: f.motivo || "",
      "Puede aplicar": f.puede_aplicar ? "SI" : "NO",
    }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), "Camas");
    if (resultado.habitaciones?.length) {
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet(
          resultado.habitaciones.map((f) => ({
            Accion: f.accion,
            Zona: f.zona || "",
            Modulo: f.modulo || "",
            Habitacion: f.habitacion || "",
            Codigo: f.codigo || "",
            Motivo: f.motivo || "",
          }))
        ),
        "Habitaciones"
      );
    }
    if (resumen) {
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet([resumen]),
        "Resumen"
      );
    }
    const fecha = new Date().toISOString().split("T")[0];
    XLSX.writeFile(wb, `inventario_logiflex_diff_${fecha}.xlsx`);
    toast.success("Excel exportado");
  };

  const renderTablaCamas = (filas) => (
    <div className="table-responsive">
      <Table hover size="sm" className="align-middle mb-0">
        <thead>
          <tr>
            <th>Acción</th>
            <th>Zona</th>
            <th>Módulo</th>
            <th>Hab</th>
            <th>Cama</th>
            <th>Código Logiflex</th>
            <th>Motivo</th>
          </tr>
        </thead>
        <tbody>
          {filas.length === 0 ? (
            <tr>
              <td colSpan={7} className="text-center text-muted py-3">
                Sin registros en este grupo
              </td>
            </tr>
          ) : (
            filas.map((f, idx) => (
              <tr key={`${f.accion}-${f.cama_id || f.codigo || idx}`}>
                <td>
                  <Badge bg={badgeVariant(f.accion)}>{f.accion}</Badge>
                  {f.accion === "INACTIVAR" && f.puede_aplicar === false && (
                    <div className="small text-danger mt-1">
                      {f.mensaje_bloqueo || "Bloqueada"}
                    </div>
                  )}
                </td>
                <td>{f.zona}</td>
                <td>{f.modulo}</td>
                <td>{f.habitacion}</td>
                <td>{f.cama}</td>
                <td>
                  <code className="small">{f.codigo || "—"}</code>
                </td>
                <td className="small">{f.motivo}</td>
              </tr>
            ))
          )}
        </tbody>
      </Table>
    </div>
  );

  const renderTablaHabs = (filas) => (
    <div className="table-responsive">
      <Table hover size="sm" className="align-middle mb-0">
        <thead>
          <tr>
            <th>Acción</th>
            <th>Zona</th>
            <th>Módulo</th>
            <th>Habitación</th>
            <th>Código</th>
            <th>Motivo</th>
          </tr>
        </thead>
        <tbody>
          {filas.length === 0 ? (
            <tr>
              <td colSpan={6} className="text-center text-muted py-3">
                Sin registros
              </td>
            </tr>
          ) : (
            filas.map((f, idx) => (
              <tr key={`hab-${f.accion}-${f.habitacion_id || f.codigo || idx}`}>
                <td>
                  <Badge bg={badgeVariant(f.accion)}>{f.accion}</Badge>
                </td>
                <td>{f.zona}</td>
                <td>{f.modulo}</td>
                <td>{f.habitacion}</td>
                <td>
                  <code className="small">{f.codigo || "—"}</code>
                </td>
                <td className="small">{f.motivo}</td>
              </tr>
            ))
          )}
        </tbody>
      </Table>
    </div>
  );

  return (
    <div className="dh-reporte-bg">
      <style>{`
        .dh-reporte-bg {
          background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
          min-height: 100vh;
          padding-bottom: 2rem;
        }
        .dh-main-header {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          padding: 2rem 0;
          margin-bottom: 2rem;
          border-radius: 0 0 30px 30px;
          box-shadow: 0 10px 30px rgba(102, 126, 234, 0.2);
        }
        .dh-main-title { font-size: 2rem; font-weight: 700; margin: 0; }
        .dh-main-subtitle { font-size: 1rem; opacity: 0.9; margin: 0.5rem 0 0 0; }
        .dh-card {
          border: none;
          border-radius: 20px;
          box-shadow: 0 15px 35px rgba(0,0,0,0.1);
          background: white;
          margin-bottom: 1.5rem;
        }
        .dh-card-header {
          border-radius: 20px 20px 0 0;
          padding: 1.25rem 1.5rem;
          border: none;
          font-weight: 700;
        }
        .dh-card-header-primary {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
        }
        .btn-dh-primary {
          background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
          border: none; color: white; border-radius: 12px; padding: 10px 18px; font-weight: 600;
        }
        .btn-dh-secondary {
          background: linear-gradient(135deg, #6b7280 0%, #374151 100%);
          border: none; color: white; border-radius: 12px; padding: 10px 18px; font-weight: 600;
        }
        .btn-dh-info {
          background: linear-gradient(135deg, #00c6ff 0%, #0072ff 100%);
          border: none; color: white; border-radius: 12px; padding: 10px 18px; font-weight: 600;
        }
        .dh-form-label { font-weight: 600; color: #374151; }
        .dh-form-control { border-radius: 10px; }
        .dh-alert-info {
          background: linear-gradient(135deg, #e0f2fe 0%, #dbeafe 100%);
          border: 1px solid #93c5fd;
          border-radius: 12px;
          padding: 1rem 1.25rem;
          color: #1e40af;
        }
        .stat-box {
          text-align: center;
          padding: 1rem;
          border-radius: 12px;
          background: #f8fafc;
          border: 1px solid #e2e8f0;
        }
        .stat-box .num { font-size: 1.6rem; font-weight: 700; color: #334155; }
        .stat-box .lbl { font-size: 0.8rem; color: #64748b; }
      `}</style>

      <div className="dh-main-header">
        <Container>
          <h1 className="dh-main-title">
            <FaBed className="me-2" />
            Inventario Alojamiento Logiflex
          </h1>
          <p className="dh-main-subtitle">
            Cargue la plantilla de inventario y valide qué habitaciones/camas
            crear o inactivar para homologar SGC con Logiflex
          </p>
        </Container>
      </div>

      <Container>
        <div className="dh-alert dh-alert-info mb-4">
          Use el archivo <strong>plantilla-inventario-alojamiento-final.xlsx</strong>{" "}
          (hojas Habitaciones y Camas). Se filtra por hotel (por defecto CAMPAMENTO).
          Las filas ACTIVO del Excel deben existir activas en SGC; las activas en SGC
          que no estén en el Excel se proponen para inactivar.
        </div>

        <Card className="dh-card">
          <div className="dh-card-header dh-card-header-primary">
            Carga y validación
          </div>
          <Card.Body>
            <Row className="g-3 align-items-end">
              <Col md={4}>
                <Form.Label className="dh-form-label">Archivo Excel</Form.Label>
                <Form.Control
                  type="file"
                  accept=".xlsx,.xls"
                  className="dh-form-control"
                  onChange={handleFileChange}
                />
              </Col>
              <Col md={3}>
                <Form.Label className="dh-form-label">Hotel</Form.Label>
                <Form.Control
                  className="dh-form-control"
                  value={hotel}
                  onChange={(e) => setHotel(e.target.value)}
                  placeholder="CAMPAMENTO"
                />
              </Col>
              <Col md={5} className="d-flex flex-wrap gap-2">
                <Button
                  className="btn-dh-primary"
                  onClick={validar}
                  disabled={loading || !file}
                >
                  {loading ? (
                    <>
                      <Spinner size="sm" className="me-2" /> Validando...
                    </>
                  ) : (
                    <>
                      <FaSync className="me-2" /> Validar diff
                    </>
                  )}
                </Button>
                <Button
                  className="btn-dh-info"
                  onClick={exportar}
                  disabled={!resultado?.camas?.length}
                >
                  <FaFileExcel className="me-2" /> Exportar
                </Button>
                <Button
                  className="btn-dh-secondary"
                  onClick={aplicar}
                  disabled={aplicando || !resultado?.success}
                >
                  {aplicando ? (
                    <>
                      <Spinner size="sm" className="me-2" /> Aplicando...
                    </>
                  ) : (
                    <>
                      <FaCheckCircle className="me-2" /> Aplicar cambios
                    </>
                  )}
                </Button>
              </Col>
            </Row>

            <Row className="mt-3 g-3">
              <Col md={4}>
                <Form.Check
                  type="checkbox"
                  id="opt-crear"
                  label="Al aplicar: crear faltantes"
                  checked={opciones.crear}
                  onChange={(e) =>
                    setOpciones((o) => ({ ...o, crear: e.target.checked }))
                  }
                />
              </Col>
              <Col md={4}>
                <Form.Check
                  type="checkbox"
                  id="opt-reactivar"
                  label="Al aplicar: reactivar inactivas"
                  checked={opciones.reactivar}
                  onChange={(e) =>
                    setOpciones((o) => ({ ...o, reactivar: e.target.checked }))
                  }
                />
              </Col>
              <Col md={4}>
                <Form.Check
                  type="checkbox"
                  id="opt-inactivar"
                  label="Al aplicar: inactivar sobrantes"
                  checked={opciones.inactivar}
                  onChange={(e) =>
                    setOpciones((o) => ({ ...o, inactivar: e.target.checked }))
                  }
                />
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {resumen && (
          <Row className="g-3 mb-4">
            <Col xs={6} md={3}>
              <div className="stat-box">
                <div className="num text-primary">{resumen.camas_crear || 0}</div>
                <div className="lbl">Camas a crear</div>
              </div>
            </Col>
            <Col xs={6} md={3}>
              <div className="stat-box">
                <div className="num text-danger">{resumen.camas_inactivar || 0}</div>
                <div className="lbl">Camas a inactivar</div>
              </div>
            </Col>
            <Col xs={6} md={3}>
              <div className="stat-box">
                <div className="num text-info">{resumen.camas_reactivar || 0}</div>
                <div className="lbl">Camas a reactivar</div>
              </div>
            </Col>
            <Col xs={6} md={3}>
              <div className="stat-box">
                <div className="num text-success">{resumen.camas_ok || 0}</div>
                <div className="lbl">Camas OK</div>
              </div>
            </Col>
            <Col xs={6} md={3}>
              <div className="stat-box">
                <div className="num">{resumen.habitaciones_crear || 0}</div>
                <div className="lbl">Habitaciones a crear</div>
              </div>
            </Col>
            <Col xs={6} md={3}>
              <div className="stat-box">
                <div className="num">{resumen.habitaciones_inactivar || 0}</div>
                <div className="lbl">Habitaciones sobrantes</div>
              </div>
            </Col>
            <Col xs={6} md={3}>
              <div className="stat-box">
                <div className="num">{resumen.excel_camas_activas || 0}</div>
                <div className="lbl">Excel camas ACTIVAS</div>
              </div>
            </Col>
            <Col xs={6} md={3}>
              <div className="stat-box">
                <div className="num text-warning">
                  {resumen.camas_inactivar_bloqueadas || 0}
                </div>
                <div className="lbl">Inactivar bloqueadas</div>
              </div>
            </Col>
          </Row>
        )}

        {resultado && (
          <Card className="dh-card">
            <div className="dh-card-header dh-card-header-primary">
              Detalle del diff
            </div>
            <Card.Body>
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Control
                    className="dh-form-control"
                    placeholder="Buscar zona, módulo, habitación, código..."
                    value={busqueda}
                    onChange={(e) => setBusqueda(e.target.value)}
                  />
                </Col>
              </Row>

              <Tabs activeKey={tab} onSelect={(k) => setTab(k || "crear")} className="mb-3">
                <Tab eventKey="crear" title={`Crear camas (${camasCrear.length})`}>
                  {renderTablaCamas(camasCrear)}
                </Tab>
                <Tab
                  eventKey="inactivar"
                  title={`Inactivar camas (${camasInactivar.length})`}
                >
                  {renderTablaCamas(camasInactivar)}
                </Tab>
                <Tab
                  eventKey="reactivar"
                  title={`Reactivar (${camasReactivar.length})`}
                >
                  {renderTablaCamas(camasReactivar)}
                </Tab>
                <Tab eventKey="ok" title={`OK (${camasOk.length})`}>
                  {renderTablaCamas(camasOk)}
                </Tab>
                <Tab
                  eventKey="hab-crear"
                  title={`Hab. crear (${habsCrear.length})`}
                >
                  {renderTablaHabs(habsCrear)}
                </Tab>
                <Tab
                  eventKey="hab-inact"
                  title={`Hab. sobrantes (${habsInactivar.length})`}
                >
                  {renderTablaHabs(habsInactivar)}
                </Tab>
              </Tabs>
            </Card.Body>
          </Card>
        )}
      </Container>
      <ToastContainer position="bottom-right" />
    </div>
  );
};

export default InventarioAlojamientoLogiflex;
